import { ModelProvider } from "src/clients/agent-management";
import {
  ModelHosting,
  ModelLifecycle,
  ModelOption,
  ModelRegion,
} from "src/genai/ModelSelector/types";

/** A model the user should avoid for new work (superseded or deprecated). */
export const isOlderModel = (model: ModelOption): boolean =>
  model.lifecycle != null && model.lifecycle !== "current";

/**
 * The models the picker offers. Deprecated models are on their way out at the
 * provider so we filter them.
 */
export const selectableModels = (
  models: ModelOption[],
  selectedModelId: string | undefined,
): ModelOption[] =>
  models.filter(
    (model) => model.id === selectedModelId || model.lifecycle !== "deprecated",
  );

/**
 * Where inference runs, determined purely by who owns the server — orthogonal to
 * the routing region. Bedrock runs on AWS (surfaced as "Taktile-hosted"); every
 * other provider uses its own managed endpoint. A globally-routed Bedrock model
 * is still AWS-hosted; its residency is reported separately as "Global".
 */
export const hostingForModel = (card: {
  provider: ModelProvider;
}): ModelHosting => (card.provider === "bedrock" ? "aws" : "managed");

/** The residency literals the backend sends today. */
const REGION_BY_LITERAL: Record<string, ModelRegion> = {
  us: "us",
  eu: "eu",
  au: "au",
  apac: "apac",
  ca: "ca",
  sa: "sa",
  global: "global",
};

/**
 * Map the backend routing region to a residency facet.
 *
 * Anything we can't place falls back to "global", the weakest claim we can
 * make. The backend resolves every supported model/region pair, so this only
 * catches a label we don't know yet.
 */
export const regionFromRouting = (
  routingTargetRegion: string | null | undefined,
): ModelRegion => {
  const value = (routingTargetRegion ?? "").trim().toLowerCase();

  const literal = REGION_BY_LITERAL[value];
  if (literal) return literal;

  // Older backends report a human-readable label, sometimes compound ("US/CA",
  // which is the US geography rather than the Canada pin). Only the geography
  // labels ever appeared there, so matching the two-letter country codes by
  // substring here would misread unrelated words.
  if (value.includes("apac")) return "apac";
  if (value.includes("eu")) return "eu";
  if (value.includes("us")) return "us";
  if (value.includes("au")) return "au";
  return "global";
};

/**
 * Lifecycle facet: a deprecated status wins, then a set `superseded_by` marks a
 * superseded model, otherwise it is current.
 */
export const lifecycleFor = (card: {
  status?: string | null;
  superseded_by?: string | null;
}): ModelLifecycle => {
  if (card.status === "deprecated") return "deprecated";
  if (card.superseded_by != null) return "superseded";
  return "current";
};

/**
 * The model we nudge toward: the newest one in this model's line. The backend
 * resolves the whole successor chain (`newest_alternative`), so we never walk
 * `superseded_by`/`deprecated_alternative` here
 */
export const successorIdFor = (card: {
  newest_alternative?: string | null;
}): string | undefined => card.newest_alternative ?? undefined;

/**
 * The model that serves this one's traffic once its retirement is in force.
 *
 * Distinct from {@link successorIdFor}: the announced replacement may itself
 * have been superseded since, in which case traffic reroutes to it while the
 * upgrade we recommend is the newer one.
 */
export const rerouteTargetIdFor = (card: {
  serving_alternative?: string | null;
}): string | undefined => card.serving_alternative ?? undefined;
